/**
 * ActionService - Handles component actions and state updates
 * Centralizes action execution and component state management
 */

import { ActionTypes, ActionFactory, actionDispatcher } from '../utils/ActionSystem.js';
import { updateComponentToggleState } from './ComponentService.js';


/**
 * PageTimeoutManager - Manages WCAG 2.2.1 compliant page timeouts
 * Handles timeout execution, warnings, extensions, and navigation
 */
class PageTimeoutManager {
  constructor() {
    this.timeoutId = null;
    this.warningTimeoutId = null;
    this.currentPage = null;
    this.onPageChange = null;
    this.pages = null;
    this.isWarningActive = false;
    this.extensionCount = 0;
    this.maxExtensions = 3; // WCAG recommendation
  }

  /**
   * Start timeout for a page if enabled
   * @param {Object} page - Page object with timeout settings
   * @param {Map} pages - Available pages map
   * @param {Function} onPageChange - Page change callback
   */
  startPageTimeout(page, pages, onPageChange) {
    // Clear any existing timeouts
    this.clearTimeouts();
    
    if (!page || !page.getTimeoutSettings().enabled) {
      console.log('â° PageTimeoutManager: Timeout not enabled for page:', page?.name);
      return;
    }

    const timeoutSettings = page.getTimeoutSettings();
    
    // Validate WCAG compliance
    if (!page.isWCAGTimeoutCompliant()) {
      console.warn('âš ï¸ PageTimeoutManager: Page timeout settings not WCAG compliant:', page.name);
      return;
    }

    this.currentPage = page;
    this.pages = pages;
    this.onPageChange = onPageChange;
    this.extensionCount = 0;
    this.isWarningActive = false;

    const totalTimeout = timeoutSettings.delay * 1000; // Convert to milliseconds
    const warningTime = timeoutSettings.warningTime * 1000;
    const warningDelay = totalTimeout - warningTime;

    console.log(`â° PageTimeoutManager: Starting timeout for page ${page.name}:`, {
      totalTimeout: totalTimeout / 1000 + 's',
      warningDelay: warningDelay / 1000 + 's',
      targetPage: timeoutSettings.targetPage
    });

    // Set warning timeout
    if (warningDelay > 0) {
      this.warningTimeoutId = setTimeout(() => {
        this.showTimeoutWarning();
      }, warningDelay);
    }

    // Set main timeout
    this.timeoutId = setTimeout(() => {
      this.executeTimeout();
    }, totalTimeout);
  }

  /**
   * Show WCAG compliant timeout warning
   */
  showTimeoutWarning() {
    if (this.isWarningActive) return;
    
    this.isWarningActive = true;
    const timeoutSettings = this.currentPage.getTimeoutSettings();
    
    console.log('âš ï¸ PageTimeoutManager: Showing timeout warning');
    
    // Create accessible warning dialog
    const warningDialog = this.createWarningDialog(timeoutSettings);
    document.body.appendChild(warningDialog);
    
    // Focus the dialog for screen readers
    warningDialog.focus();
    
    // Emit custom event for UI frameworks to handle
    window.dispatchEvent(new CustomEvent('wcag-timeout-warning', {
      detail: {
        page: this.currentPage.name,
        remainingTime: timeoutSettings.warningTime,
        allowExtension: timeoutSettings.allowExtension,
        targetPage: timeoutSettings.targetPage
      }
    }));
  }

  /**
   * Create accessible timeout warning dialog
   */
  createWarningDialog(timeoutSettings) {
    const dialog = document.createElement('div');
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-labelledby', 'timeout-warning-title');
    dialog.setAttribute('aria-describedby', 'timeout-warning-description');
    dialog.setAttribute('tabindex', '-1');
    dialog.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border: 2px solid #d73527;
      border-radius: 8px;
      padding: 24px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      max-width: 500px;
      font-family: Arial, sans-serif;
    `;
    
    dialog.innerHTML = `
      <h2 id="timeout-warning-title" style="margin: 0 0 16px 0; color: #d73527;">
        Page Timeout Warning
      </h2>
      <div id="timeout-warning-description" style="margin-bottom: 20px;">
        This page will automatically return in ${timeoutSettings.warningTime} seconds 
        ${timeoutSettings.targetPage ? `to ${timeoutSettings.targetPage}` : 'to the previous page'}.
      </div>
      <div style="display: flex; gap: 12px; justify-content: flex-end;">
        ${timeoutSettings.allowExtension ? 
          `<button id="extend-timeout-btn" style="
            padding: 8px 16px; 
            background: #0066cc; 
            color: white; 
            border: none; 
            border-radius: 4px;
            cursor: pointer;
          ">Extend Time</button>` : ''
        }
        <button id="stay-here-btn" style="
          padding: 8px 16px; 
          background: #28a745; 
          color: white; 
          border: none; 
          border-radius: 4px;
          cursor: pointer;
        ">Stay Here</button>
        <button id="go-back-btn" style="
          padding: 8px 16px; 
          background: #6c757d; 
          color: white; 
          border: none; 
          border-radius: 4px;
          cursor: pointer;
        ">Go Back Now</button>
      </div>
    `;

    // Add event listeners
    const stayBtn = dialog.querySelector('#stay-here-btn');
    const goBackBtn = dialog.querySelector('#go-back-btn');
    const extendBtn = dialog.querySelector('#extend-timeout-btn');

    stayBtn?.addEventListener('click', () => {
      this.cancelTimeout();
      this.removeWarningDialog(dialog);
    });

    goBackBtn?.addEventListener('click', () => {
      this.executeTimeout();
      this.removeWarningDialog(dialog);
    });

    if (extendBtn && timeoutSettings.allowExtension) {
      extendBtn.addEventListener('click', () => {
        this.extendTimeout();
        this.removeWarningDialog(dialog);
      });
    }

    return dialog;
  }

  /**
   * Remove warning dialog from DOM
   */
  removeWarningDialog(dialog) {
    if (dialog && dialog.parentNode) {
      dialog.parentNode.removeChild(dialog);
    }
    this.isWarningActive = false;
  }

  /**
   * Extend timeout period (WCAG compliance)
   */
  extendTimeout() {
    if (!this.currentPage || this.extensionCount >= this.maxExtensions) {
      console.warn('âš ï¸ PageTimeoutManager: Maximum extensions reached or no current page');
      return;
    }

    const timeoutSettings = this.currentPage.getTimeoutSettings();
    if (!timeoutSettings.allowExtension) {
      console.warn('âš ï¸ PageTimeoutManager: Extensions not allowed for this page');
      return;
    }

    this.extensionCount++;
    const extensionTime = (timeoutSettings.extensionTime || timeoutSettings.delay) * 1000;
    
    console.log(`â° PageTimeoutManager: Extending timeout (${this.extensionCount}/${this.maxExtensions}) by ${extensionTime/1000}s`);
    
    // Clear existing timeouts
    this.clearTimeouts();
    
    // Restart timeout with extension time
    this.timeoutId = setTimeout(() => {
      this.executeTimeout();
    }, extensionTime);

    // Set new warning timeout
    const warningDelay = extensionTime - (timeoutSettings.warningTime * 1000);
    if (warningDelay > 0) {
      this.warningTimeoutId = setTimeout(() => {
        this.showTimeoutWarning();
      }, warningDelay);
    }
  }

  /**
   * Execute the timeout navigation
   */
  executeTimeout() {
    if (!this.currentPage || !this.onPageChange || !this.pages) {
      console.warn('âš ï¸ PageTimeoutManager: Cannot execute timeout - missing context');
      return;
    }

    const timeoutSettings = this.currentPage.getTimeoutSettings();
    const targetPage = timeoutSettings.targetPage;

    console.log(`â° PageTimeoutManager: Executing timeout navigation from ${this.currentPage.name} to ${targetPage || 'default'}`);

    // Remove any warning dialogs
    const warningDialogs = document.querySelectorAll('[role="alertdialog"]');
    warningDialogs.forEach(dialog => {
      if (dialog.parentNode) {
        dialog.parentNode.removeChild(dialog);
      }
    });

    // Execute navigation
    if (targetPage && this.pages.has(targetPage)) {
      this.onPageChange(targetPage, this.pages.get(targetPage) || []);
      console.log(`âœ… PageTimeoutManager: Navigated to ${targetPage} due to timeout`);
    } else {
      console.warn(`âš ï¸ PageTimeoutManager: Target page ${targetPage} not found, staying on current page`);
    }

    // Emit timeout executed event
    window.dispatchEvent(new CustomEvent('wcag-timeout-executed', {
      detail: {
        fromPage: this.currentPage.name,
        toPage: targetPage,
        extensionsUsed: this.extensionCount
      }
    }));

    this.clearTimeouts();
  }

  /**
   * Cancel current timeout
   */
  cancelTimeout() {
    console.log('â° PageTimeoutManager: Timeout cancelled by user');
    this.clearTimeouts();
    
    // Emit timeout cancelled event
    window.dispatchEvent(new CustomEvent('wcag-timeout-cancelled', {
      detail: {
        page: this.currentPage?.name,
        extensionsUsed: this.extensionCount
      }
    }));
  }

  /**
   * Clear all active timeouts
   */
  clearTimeouts() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    this.isWarningActive = false;
  }

  /**
   * Get current timeout status
   */
  getStatus() {
    return {
      active: this.timeoutId !== null,
      warningActive: this.isWarningActive,
      currentPage: this.currentPage?.name,
      extensionCount: this.extensionCount,
      maxExtensions: this.maxExtensions
    };
  }
}

// Create singleton instance
export const pageTimeoutManager = new PageTimeoutManager();

/**
 * Execute a component action
 * 
 * @param {string} componentId - ID of the component that triggered the action
 * @param {Object} component - The component object
 * @param {Object} context - Action execution context
 * @param {Function} onComponentUpdate - Callback for component updates
 * @returns {Object} - Action execution result
 */
export async function executeComponentAction(componentId, component, context, onComponentUpdate) {
  console.log('ðŸ”§ ActionService: Executing action for component:', componentId);
  
  if (!component || !component.behavior) {
    console.warn('ActionService: No behavior defined for component:', componentId);
    return { success: false, error: 'No behavior defined' };
  }

  
  // Handle toggle state update before executing action (if applicable)
  let updatedComponent = component;
  if (component.toggleState?.isToggleable && component.behavior?.behaviorType === 'toggle') {
    const currentState = component.toggleState.currentState;
    const states = component.toggleState.states;
    
    if (states && Object.keys(states).length > 0) {
      // Determine next state
      const stateKeys = Object.keys(states);
      const currentIndex = stateKeys.indexOf(currentState);
      const nextIndex = (currentIndex + 1) % stateKeys.length;
      const nextState = stateKeys[nextIndex];
      
      console.log('ðŸ”§ ActionService: Toggling state:', currentState, 'â†’', nextState);
      
      // Update component state
      updatedComponent = updateComponentToggleState(component, nextState);
      
      // Notify parent component of the update
      if (onComponentUpdate) {
        onComponentUpdate(componentId, updatedComponent);
      }
    }
  }

  // Create action object from behavior
  const action = ActionFactory.fromBehavior(component.behavior);
  
  // Add toggle state to context
  const enhancedContext = {
    ...context,
    componentId,
    toggleState: updatedComponent.toggleState?.currentState
  };
  
  // Add toggle value if component is toggleable
  if (updatedComponent.toggleState?.isToggleable && updatedComponent.toggleState?.states) {
    const currentState = updatedComponent.toggleState.currentState;
    const stateValue = updatedComponent.toggleState.states[currentState]?.value;
    enhancedContext.toggleValue = stateValue;
    console.log('ðŸ”§ ActionService: Toggle value for state', currentState, ':', stateValue);
  }
  
  // Execute action
  const result = await actionDispatcher.executeAction(action, enhancedContext);
  
  if (result && !result.success) {
    console.error('âŒ ActionService: Action failed:', result.error);
    return result;
  } else if (result && result.success) {
    console.log('âœ… ActionService: Action executed successfully:', result);
    // If the action succeeded and returned a value, update the text field's value
    if (result.result !== undefined && component.type === 'textfield' && typeof onComponentUpdate === 'function') {
      onComponentUpdate(componentId, {
        ...component,
        content: {
          ...component.content,
          primaryText: result.result
        }
      });
    }
    return result;
  } else {
    // This is a custom action, it wouldn't use standard success/error structure
    return { success: true };
  }  
}

/**
 * Handle navigation action
 * 
 * @param {string} targetPage - Target page name
 * @param {Map} pages - Available pages
 * @param {Function} onPageChange - Callback for page changes
 * @returns {boolean} - Success status
 */
export function handleNavigationAction(targetPage, pages, onPageChange) {
  console.log('ðŸ”§ ActionService: Handling navigation to:', targetPage);
  
  if (targetPage && pages.has(targetPage)) {
    // Clear any existing timeouts when navigating
    pageTimeoutManager.clearTimeouts();
    
    if (onPageChange) {
      onPageChange(targetPage, pages.get(targetPage) || []);
    }
    
    // Start timeout for new page if configured
    const newPage = pages.get(targetPage);
    if (newPage) {
      pageTimeoutManager.startPageTimeout(newPage, pages, onPageChange);
    }
    
    console.log(`âœ… ActionService: Navigated to page: ${targetPage}`);
    return true;
  } else {
    console.error(`âŒ ActionService: Page not found: ${targetPage}`);
    return false;
  }
}

/**
 * Create action context for UI components
 * 
 * @param {Object} params - Context parameters
 * @returns {Object} - Action context
 */
export function createActionContext({
  componentId,
  currentPage,
  currentConfigId,
  pages,
  onPageChange,
  onComponentUpdate,
  userId = 'user',
  profileType = 'general'
}) {
  return {
    componentId,
    currentPage,
    currentConfigId,
    navigateToPage: (targetPage) => {
      return handleNavigationAction(targetPage, pages, onPageChange);
    },
    updateComponent: onComponentUpdate,
    userId,
    profileType
  };
}

/**
 * Validate component behavior
 * 
 * @param {Object} behavior - Component behavior object
 * @returns {Object} - Validation result
 */
export function validateComponentBehavior(behavior) {
  if (!behavior) {
    return { valid: false, error: 'No behavior defined' };
  }
  
  if (!behavior.action) {
    return { valid: false, error: 'No action defined in behavior' };
  }
  
  // Validate vehicle function behaviors
  if (behavior.action === ActionTypes.VehicleFunction) {
    if (!behavior.category) {
      return { valid: false, error: 'Vehicle function missing category' };
    }
    if (!behavior.functionName) {
      return { valid: false, error: 'Vehicle function missing functionName' };
    }
  }
  
  // Validate navigation behaviors
  if (behavior.action === ActionTypes.Navigate) {
    if (!behavior.targetPage) {
      return { valid: false, error: 'Navigation action missing targetPage' };
    }
  }
  
  return { valid: true };
}

/**
 * Initialize page timeout when a page is first loaded
 * Call this when a page becomes active (e.g., on initial load or navigation)
 * 
 * @param {Object} page - Page object with timeout settings
 * @param {Map} pages - Available pages map
 * @param {Function} onPageChange - Page change callback
 */
export function initializePageTimeout(page, pages, onPageChange) {
  console.log('â° ActionService: Initializing timeout for page:', page?.name);
  pageTimeoutManager.startPageTimeout(page, pages, onPageChange);
}

/**
 * Get current timeout status for debugging/monitoring
 * 
 * @returns {Object} - Current timeout manager status
 */
export function getTimeoutStatus() {
  return pageTimeoutManager.getStatus();
}
